import {
  Component,
  DestroyRef,
  EventEmitter,
  inject,
  Input,
  OnDestroy,
  OnInit,
  Output
} from "@angular/core";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { FormBuilder, FormGroup, Validators } from "@angular/forms";
import { ActivatedRoute } from "@angular/router";
import { SelectionService } from "src/app/shared/services/selection/selection.service";
import {
  RnsidebarService,
  SidebarComponents,
} from "src/app/shared/services/sidebar/rnsidebar.service";
import { Guid } from "src/app/shared/utilities/guid/guid.utilities";
import { TableSelectable } from "../../../../core/interfaces/table-selectable";
import { DataRetrievalParameters } from "../../../../core/models/data-retrieval-parameters";
import { LicenseService } from "../../../services/rnapi2-service/apis/api";
import {
  RnCommonIdMulti,
  RnCommonSearch,
  RnLicensesWithUsersVM,
  RnLicenseUpdateDTO,
  RnOrganizationsProfileVM,
  RnUserLicensesRemoveUser,
  RnUsersForOrgSubOrgSearchForLicenseVM,
} from "../../../services/rnapi2-service/models/models";
import { GridCheckLimitColumnConfiguration } from "../../third-party-wrappers/grid/configuration/grid-check-limit-column-configuration";
import { GridColumnConfiguration } from "../../third-party-wrappers/grid/configuration/grid-column-configuration";
import { GridConfiguration } from "../../third-party-wrappers/grid/configuration/grid-configuration";
import { LicenseAssignConfig } from "../license-assign/license-assign-config";
import { SignalrPollingBaseMessage } from "../../../../core/models/signalr/signalr-polling-base-message";
import { SignalRService } from "../../../notifications/signalr.service";
import { SignalrPollingService } from "../../../services/signalr-polling/signalr-polling.service";
import { ProcessingEventData } from "../../../../core/models/processing-event-data";
import { processEventStateTypes } from "../../../../core/enums/processEventStateTypes";
import { ToasterService, ToastType } from "@rn-platform/frontend-shared-data-access-toaster";
import { LicenseEditModalService } from "../license-edit/license-edit-modal.service";

class LicenseUserData extends RnUsersForOrgSubOrgSearchForLicenseVM implements TableSelectable {
  RnTableSelected: boolean;
}
@Component({
    selector: "app-license-details",
    templateUrl: "./license-details.component.html",
    styleUrls: ["./license-details.component.scss"],
    standalone: false
})
export class LicenseDetailsComponent implements OnInit, OnDestroy {
  private route = inject(ActivatedRoute);
  private licenseService = inject(LicenseService);
  private formBuilder = inject(FormBuilder);
  private toasterService = inject(ToasterService);
  private rnsidebarService = inject(RnsidebarService);
  private selectionService = inject(SelectionService);
  private signalrPollingService = inject(SignalrPollingService);
  private signalRService = inject(SignalRService);
  private readonly destroyRef = inject(DestroyRef);

  @Input() LicenseId: string;
  @Output() ChangeState = new EventEmitter<any>();
  @Output() ProcessingEvent = new EventEmitter<ProcessingEventData>();
  organizationId: number;
  license: RnLicensesWithUsersVM = new RnLicensesWithUsersVM();
  licenseGridConfiguration: GridConfiguration = new GridConfiguration();
  autoAssignInProgress = false;
  selectedOrgStatus: string;
  private signalRHandlerID: Guid;
  formGroup: FormGroup;
  refreshUserGuid: Guid;
  canShowActions = false;
  canShowEdit = false;
  isUnassignUsersDisabled = true;
  canAssignUsersToLicense = false;
  private eventData = new ProcessingEventData();
  public isAssigned = false;
  public counter = 0;
  private readonly licenseEditModalService: LicenseEditModalService = inject(LicenseEditModalService);

  ngOnInit(): void {
    this.eventData.type = processEventStateTypes.updating;
    this.ProcessingEvent.emit(this.eventData);
    this.formGroup = this.formBuilder.group({
      license1: ["", Validators.required.bind(Validators)],
      license2: ["", Validators.required.bind(Validators)],
    });

    this.route.queryParamMap.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((p) => {
      this.organizationId = Number(
        p.has("subOrgId") ? p.get("subOrgId") : this.route.snapshot.params.id,
      );
    });
    
    this.selectionService.SelectedOrgProfile.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((p) => {
      this.selectedOrg = p;
      this.selectedOrgStatus = this.selectedOrg?.Status;
    });
    this.rnCommonSearch.AffectedOrganizationId = this.organizationId;
    this.rnCommonSearch.Id = this.LicenseId;
    this.rnCommonSearch.pageNumber = 0;
    this.rnCommonSearch.pageSize = 10;
    this.rnCommonSearch.isDescendingOrder = false;
    this.rnCommonSearch.OrderByText = "orderByFullName";

    const payload: RnCommonIdMulti = {
      Ids: [this.LicenseId],
      AffectedOrganizationId: this.organizationId,
    };
    this.licenseService
      .apiV2LicensesLicensesgetbyidPost(payload)
      .subscribe((r) => {
        this.license = r.data[0];
        this.setupGridColumns();
        this.performSearch();
        this.canShowActions = this.canShowEdit = this.licenseIsByol();
        this.canAssignUsersToLicense = this.getCanAssignUsersToLicense();
      });
    this.signalrPollingService.AddOrgTrackingInfo(this.organizationId);
    this.signalRService.messages.subscribe((message) => {
      const notification = JSON.parse(message) as SignalrPollingBaseMessage;
      if (
        notification &&
        notification.Payload &&
        notification.Payload.AreaDataType.toUpperCase() ===
          "LICENSE_ASSIGN_PAYLOAD" &&
        notification.Payload.Area.toUpperCase() == "ORG" &&
        notification.Payload.AreaID == this.organizationId
      ) {
        this.performSearch();
      }
    });
  }

  ngOnDestroy(): void {
    if (this.signalRHandlerID) {
      this.signalrPollingService.RemoveMessageHandler(this.signalRHandlerID);
    }
    this.signalrPollingService.RemoveOrgTrackingInfo(this.organizationId);
  }

  setupGridColumns() {
    this.licenseGridConfiguration.FirstRow = 0;
    this.licenseGridConfiguration.ColumnConfiguration = [];
    this.licenseGridConfiguration.ShowCountHeader = true;
    this.licenseGridConfiguration.HeaderClass = "userCount";
    this.licenseGridConfiguration.GridClass =
      "tableHeadingColorClass tableRowHeight";
    this.licenseGridConfiguration.CountHeaderItemsName =
      "Users Assigned to License";
    this.licenseGridConfiguration.getClassForCell = (
      col: GridColumnConfiguration,
      rowData: any,
    ) => {
      return col.Class + (col.FieldName == "UserEmail" ? " break" : "");
    };

    this.selectionColumnConfig = new GridCheckLimitColumnConfiguration(
      "",
      "",
      "checkboxCol",
      "",
    );
    this.selectionColumnConfig.SelectAllChange = (
      $event,
      allSelected: boolean,
    ) => {
      if (allSelected) {
        this.selectionColumnConfig.SelectedItems = [];
        this.currUsers.forEach((l) => {
          const d = l as LicenseUserData;
          d.RnTableSelected = true;
          this.selectionColumnConfig.SelectedItems.push(d);
        });
        this.isUnassignUsersDisabled =
          this.licenseGridConfiguration.TotalRecords == 0 ? true : false;
      } else {
        this.selectionColumnConfig.SelectedItems.forEach((i) => {
          i.RnTableSelected = false;
        });
        this.selectionColumnConfig.SelectedItems = [];
        this.isUnassignUsersDisabled = true;
      }
    };
    this.selectionColumnConfig.SelectAllAvailable = true;
    this.selectionColumnConfig.SelectedItems = [];
    this.selectionColumnConfig.CheckChange = ($event, rowData: any) => {
      const l = rowData as LicenseUserData;
      if (l.RnTableSelected) {
        this.isUnassignUsersDisabled = false;
        this.licenseGridConfiguration.RnSelectAll =
          this.currUsers.length ===
          this.selectionColumnConfig.SelectedItems.length;
      } else {
        this.isUnassignUsersDisabled =
          this.selectionColumnConfig.SelectedItems.length <= 0;
        this.licenseGridConfiguration.RnSelectAll = false;
      }
    };

    this.licenseGridConfiguration.ColumnConfiguration.push(
      this.selectionColumnConfig,
    );
    this.licenseGridConfiguration.RnSelectionEnabled = true;
    this.licenseGridConfiguration.RnSelectAll = false;

    const nameColumn = new GridColumnConfiguration(
      "FullName",
      "User",
      "",
      "orderByFullName",
    );
    this.licenseGridConfiguration.ColumnConfiguration.push(nameColumn);
    this.licenseGridConfiguration.ColumnConfiguration.push(
      new GridColumnConfiguration(
        "UserEmail",
        "Email Address",
        "",
        "orderByUserEmail",
      ),
    );
    this.licenseGridConfiguration.ColumnConfiguration.push(
      new GridColumnConfiguration(
        "AssignedPackage",
        "Package",
        "",
        "orderByAssignedPackage",
      ),
    );
    this.licenseGridConfiguration.ColumnConfiguration.push(
      new GridColumnConfiguration(
        "OrganizationName",
        "Organization",
        "",
        "orderByOrganizationName",
      ),
    );

    this.licenseGridConfiguration.setUrlData = (
      params: DataRetrievalParameters,
    ) => {
      this.rnCommonSearch.pageNumber =
        ((params.SortOrder === "" &&
          this.rnCommonSearch.OrderByText === "orderByFullName") ||
          this.rnCommonSearch.OrderByText === params.SortOrder) &&
        this.rnCommonSearch.isDescendingOrder === params.IsDescendingOrder &&
        this.rnCommonSearch.pageSize === params.PageSize
          ? params.PageNumber
          : 0;
      this.rnCommonSearch.pageSize = params.PageSize;
      this.rnCommonSearch.isDescendingOrder = params.IsDescendingOrder;
      this.rnCommonSearch.OrderByText = params.SortOrder
        ? params.SortOrder
        : "orderByFullName";
      this.rnCommonSearch.Id = this.LicenseId;
      this.rnCommonSearch.AffectedOrganizationId = this.organizationId;
      this.licenseGridConfiguration.RnSelectAll = false;
      this.isUnassignUsersDisabled = true;
      this.performSearch();
    };
  }

  public rowSelected(event): void {
    this.selectionColumnConfig.SelectedItems = event;
  }

  goBackToLicenses() {
    this.ChangeState.emit({ state: "list" });
  }

  toggleLicenseAutoAssign(event) {
    this.eventData.type = processEventStateTypes.updating;
    this.ProcessingEvent.emit(this.eventData);
    this.autoAssignInProgress = true;
    this.license.IsAutoAssignable = !this.license.IsAutoAssignable;
    // do the API thing
    const payload: RnLicenseUpdateDTO = {
      AffectedOrganizationId: this.organizationId,
      LicensesID: Number(this.LicenseId),
      ItemsToUpdate: [
        {
          Key: "IsAutoProvision",
          Value: this.license.IsAutoAssignable.toString(),
        },
      ],
      IsByol: this.license.Type == "BYOL",
    };
    this.licenseService
      .apiV2LicensesUpdatePost(payload)
      .subscribe({
        next: (r) => {
          this.autoAssignInProgress = true;
        },
        error: (err) => {
          this.autoAssignInProgress = false;
          this.eventData.type = processEventStateTypes.completed;
          this.ProcessingEvent.emit(this.eventData);
        },
        complete: () => {
          this.autoAssignInProgress = false;
          this.eventData.type = processEventStateTypes.completed;
          this.ProcessingEvent.emit(this.eventData);
        },
      });
    this.autoAssignInProgress = false;
  }

  licenseIsByol(): boolean {
    return this.license && this.license.Type == "BYOL";
  }

  editLicense() {
    this.licenseEditModalService.open(this.license);
  }

  getCanAssignUsersToLicense() {
    if (this.license.Type == "Leased") {
      if (this.currUsers && this.currUsers?.length === 1) {
        return false;
      }
      if (!this.currUsers && this.license.Users.length === 0) {
        return true;
      }
    }
    if (this.selectedOrg && this.license)
      return (
        this.selectedOrgStatus !== "Suspended" &&
        this.license &&
        (this.license.Type != "Leased" ||
          this.license.LicenseCount > this.license.Users.length)
      );
  }

  unassignUsers() {
    const payload: RnUserLicensesRemoveUser = {
      AffectedOrganizationId: this.organizationId,
      LicenseID: Number(this.LicenseId),
      UserIDs: (
        this.selectionColumnConfig
          .SelectedItems as RnUsersForOrgSubOrgSearchForLicenseVM[]
      ).map((s) => {
        return Number(s.UserID);
      }),
    };
    this.eventData.type = processEventStateTypes.updating;
    this.ProcessingEvent.emit(this.eventData);

    this.licenseService
      .apiV2LicensesRemovelicensefromusersPost(payload)
      .subscribe((r) => {
        if (r && r.Success) {
          this.signalRHandlerID = this.signalrPollingService.AddMessageHandler(
            (message) => {
              const notification = JSON.parse(
                message,
              ) as SignalrPollingBaseMessage;
              if (
                notification &&
                notification.Payload &&
                notification.Payload.AreaDataType.toUpperCase() ===
                  "LICENSE_ASSIGN_PAYLOAD" &&
                notification.Payload.Area.toUpperCase() == "ORG" &&
                notification.Payload.AreaID == this.organizationId
              ) {
                const removed =
                  notification.Payload.AreaData.includes("Removed");
                if (removed) {
                  this.licenseGridConfiguration.RnSelectAll = false;
                  this.selectionColumnConfig.SelectedItems = [];
                  // reset disable state for button after remove
                  this.isUnassignUsersDisabled = true;

                  if (this.license.Type == "Leased") {
                    this.canAssignUsersToLicense =
                      !this.getCanAssignUsersToLicense();
                    this.isUnassignUsersDisabled =
                      !this.getCanAssignUsersToLicense();
                  }
                }
              }
            },
          );
        }
      });
  }

  assignUsers(): void {
    const dialogConfig = new LicenseAssignConfig();
    dialogConfig.LicenseID = Number(this.LicenseId);
    dialogConfig.OrganizationID = this.selectedOrg.ID;
    this.rnsidebarService.trigger(
      SidebarComponents.licenseAssign,
      dialogConfig,
      "assignUsers",
    );

    this.signalRHandlerID = this.signalrPollingService.AddMessageHandler(
      (message) => {
        const notification = JSON.parse(message) as SignalrPollingBaseMessage;
        if (
          notification &&
          notification.Payload &&
          notification.Payload.AreaDataType.toUpperCase() ===
            "LICENSE_ASSIGN_PAYLOAD" &&
          notification.Payload.Area.toUpperCase() == "ORG" &&
          notification.Payload.AreaID == this.organizationId
        ) {
          const added = notification.Payload.AreaData.includes("Assigned");
          if (added) {
            this.isAssigned = true;
            this.licenseGridConfiguration.RnSelectAll = false;
            this.selectionColumnConfig.SelectedItems = [];
            if (this.license.Type == "Leased") {
              this.canAssignUsersToLicense = !this.getCanAssignUsersToLicense();
              this.isUnassignUsersDisabled = !this.getCanAssignUsersToLicense();
            }
          }
        }
      },
    );
  }

  private performSearch(result?: number) {
    this.eventData.type = processEventStateTypes.updating;
    this.ProcessingEvent.emit(this.eventData);
    this.licenseService
      .apiV2LicensesGetlicenseusersPost(this.rnCommonSearch, "response")
      .subscribe({
        next: (r) => {
          this.currUsers = r.body.data.Results;
          this.licenseGridConfiguration.loadingDataCompleted(
            r.body.data.Results,
            r.body.data.TotalNumberRecords,
            this.rnCommonSearch.pageNumber * this.rnCommonSearch.pageSize,
            this.rnCommonSearch.pageNumber,
          );
          this.eventData.type = processEventStateTypes.completed;
          this.ProcessingEvent.emit(this.eventData);
          if (this.counter !== r.body.data.TotalNumberRecords && this.isAssigned === true) {
            //added counter to make sure the toast do not get updated on each signalR record, added the flag to make sure the toast only appears on assign not each grid refresh
            if (this.license.Type == "Leased") {
              this.canAssignUsersToLicense = this.getCanAssignUsersToLicense();
            }
            this.counter = r.body.data.TotalNumberRecords;
            const message = `${r.body.data.TotalNumberRecords.toString()} Users have been assigned to the ${this.license.ItemName} License.`;
            const id = 'apphub-license-details-assign-users-success-toast';
            this.toasterService.addToast(ToastType.Success_, message, message, id, id);
          }
        },
        error: (error) => {
          console.error(error);
          this.eventData.type = processEventStateTypes.completed;
          this.ProcessingEvent.emit(this.eventData);
        },
      });
  }

  private rnCommonSearch: RnCommonSearch = new RnCommonSearch();
  private currUsers: RnUsersForOrgSubOrgSearchForLicenseVM[];
  private selectionColumnConfig: GridCheckLimitColumnConfiguration;
  selectedOrg: RnOrganizationsProfileVM;
}
